import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import LoadingAdornment from 'pages/Distribution/components/LoadingAdornment';
import distributionApi from 'services/api/distribution';
import { useSubscription } from 'services/hooks';
import { selectReleaseArtistName } from 'services/store/distribution/selectors';
import { ReleaseFieldType } from 'services/store/distribution/types';
import { selectArtists } from 'services/store/user/selectors';

type PropsType = {
  releaseId: string;
};
const ArtistNameSelector = ({ releaseId }: PropsType) => {
  const { t } = useTranslation();
  const { hasDistribution } = useSubscription();

  const artists = useSelector(selectArtists);
  const artistData = useSelector(selectReleaseArtistName(releaseId));
  const { data: artistName, loading, error } = artistData;

  const artistNameId = useMemo(() => {
    if (!artistName) {
      return null;
    }
    const currentArtist = artists.find((artist) => artist.name === artistName);

    return currentArtist?.id ?? null;
  }, [artistName, artists]);

  const [updateRelease] = distributionApi.useUpdateReleaseMutation();

  useEffect(() => {
    if (!artistNameId && artists.length > 0 && !hasDistribution) {
      updateRelease({
        releaseId,
        field: ReleaseFieldType.ArtistName,
        value: artists[0].id,
      });
    }
  }, [artistNameId, artists, releaseId, updateRelease, hasDistribution]);

  const handleChange = (event: SelectChangeEvent) => {
    const { value: newValue } = event.target;
    updateRelease({
      releaseId,
      field: ReleaseFieldType.ArtistName,
      value: newValue,
    });
  };

  const isDisabled = loading || !hasDistribution;

  return (
    <FormControl fullWidth error={!!error} disabled={isDisabled}>
      <InputLabel id="artist-name-label">
        {t(`distribution:artistName:inputLabel`)}
      </InputLabel>
      <Select
        labelId="artist-name-label"
        label={t(`distribution:artistName:inputLabel`)}
        value={artistNameId ?? ''}
        inputProps={{ ...(loading && { IconComponent: () => null }) }}
        endAdornment={loading && <LoadingAdornment />}
        onChange={handleChange}
      >
        {artists.map(({ id, name }) => (
          <MenuItem key={id} value={id}>
            {name}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default ArtistNameSelector;
